import { GRADE_LEVELS, GRADE_LEVEL_LABELS, GradeLevel } from '../domain/catalog';
import { LearnerRequestReason, LearnerRequestState, RequestBlock } from '../api/learner-explore';

/**
 * DEC-S2-S06-01: the label, in one place, on every width. Not "Buy Class": the
 * Student does not buy. It stays in English as the decision states it; a Spanish
 * label is Product's to approve.
 */
export const REQUEST_THIS_CLASS = 'Request This Class';

/** One word per lifecycle state, for the Student (DEC-S2-S06-05). */
export const REQUEST_STATE_WORDS: Record<LearnerRequestState, string> = {
  PENDING_PARENT: 'Esperando a tu familia',
  PARENT_DECLINED: 'Tu familia dijo que no',
  PARENT_APPROVED_PENDING_TEACHER: 'Esperando al profesor',
  ENROLLED: 'Ya estás inscrito',
  EXPIRED: 'Esta solicitud ya no sigue',
  UNAVAILABLE: 'Esta clase ya no está disponible',
};

/** The states that still have something to wait for. */
export const isOpenRequest = (state: LearnerRequestState): boolean =>
  state === 'PENDING_PARENT' || state === 'PARENT_APPROVED_PENDING_TEACHER';

/** Why a request ended, in words that say nothing about money. */
export const REQUEST_REASON_WORDS: Partial<Record<LearnerRequestReason, string>> = {
  OFFER_WITHDRAWN: 'La clase ya no se ofrece.',
  OFFER_CHANGED: 'La clase cambió desde que la pediste.',
  SEAT_FULL: 'Ya no quedan lugares.',
  SESSION_UNAVAILABLE: 'Ese horario ya no está disponible.',
  LEARNER_OUT_OF_RANGE: 'La clase no es para tu grado.',
  TEACHER_DECLINED: 'El profesor no pudo aceptarla.',
  ACCEPTANCE_EXPIRED: 'El profesor no respondió a tiempo.',
};

/** "2do a 5to": the range the course states, never one label against another. */
export function gradeRangeWords(gradeLevel: string[]): string {
  const positions = gradeLevel
    .map((g) => GRADE_LEVELS.indexOf(g as GradeLevel))
    .filter((i) => i >= 0);
  if (positions.length === 0) return '';
  const first = GRADE_LEVELS[Math.min(...positions)];
  const last = GRADE_LEVELS[Math.max(...positions)];
  return first === last
    ? GRADE_LEVEL_LABELS[first]
    : `${GRADE_LEVEL_LABELS[first]} a ${GRADE_LEVEL_LABELS[last]}`;
}

/** DEC-S2-S04-08: the block, with its reason. The server decides which one it is. */
export function blockExplanation(reason: RequestBlock, gradeLevel: string[]): string {
  switch (reason) {
    case 'LEARNER_OUT_OF_RANGE': {
      const range = gradeRangeWords(gradeLevel);
      return range
        ? `Esta clase es para ${range}, y tu grado no está en ese rango. Puedes leer de qué trata, pero no pedirla.`
        : 'Esta clase no es para tu grado. Puedes leer de qué trata, pero no pedirla.';
    }
    case 'ALREADY_ENROLLED':
      return 'Ya estás en esta clase.';
    case 'SEAT_FULL':
      return 'Esta clase ya no tiene lugares.';
    case 'AVAILABILITY_UNKNOWN':
      return 'Ahora mismo no pudimos comprobar si hay lugares. Prueba otra vez en un momento.';
    default:
      return 'Esta clase todavía no se puede pedir.';
  }
}

/** The rail's hues, by the name the server stores. */
export const ACCENT_COLOURS: Record<string, string> = {
  orange: '#F97316',
  blue: '#3D7BFF',
  green: '#22A97F',
  violet: '#7C5CF0',
  red: '#E5484D',
};
